import { ApolloProvider } from "@apollo/client";
import { AlarmAudioProvider } from "@jidlomat/terminal-core/contexts/AlarmAudioContext";
import { DeviceContextProvider } from "@jidlomat/terminal-core/contexts/DeviceContext";
import { useIdleWakeAction } from "@jidlomat/terminal-core/hooks/useIdleWakeAction";
import { useAppDispatch } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import { changeLanguage } from "i18next";
import { useCallback, useEffect, useRef } from "react";
import { type EventsType, IdleTimerProvider, type PresenceType } from "react-idle-timer";
import { NetworkDebugOverlay } from "./components/global/components/NetworkDebugOverlay";
import { ConnectionErrorModal } from "./components/global/modals/ConnectionErrorModal";
import { FlowHost } from "./flow/FlowHost";
import { Config } from "./services/configuration/eatSmartConfig";
import "./services/i18n/config";
import { setAppState, setLocale } from "@jidlomat/terminal-core/slices/appStateSlice";
import type { AppState } from "@jidlomat/terminal-core/types/mainTypes";
import { useFridgeApolloClient } from "@jidlomat/terminal-core/utils/ApolloClientProvider";
import { getLocale } from "@jidlomat/terminal-core/utils/browser/localStorage";
import { computeIsZombie, nextDroppedThisEpisode } from "@jidlomat/terminal-core/utils/connectionErrorState";
import { shouldTraceNetworkDebug } from "@jidlomat/terminal-core/utils/debug/networkTrace";

// react-idle-timer's default event list includes `visibilitychange` and `focus`, which fire
// constantly on iPadOS/WKWebView kiosk tablets (screen dim, Auto-Lock, Low Power Mode, app-switcher,
// Guided Access) — they were registering as "activity" and flapping `appState` ACTIVE↔IDLE, which
// blinked the fridge lights. Restrict activity detection to genuine user interaction so only real
// taps / scrolls / key input reset presence. Module-level constant = stable reference (no re-bind).
const IDLE_ACTIVITY_EVENTS: EventsType[] = ["touchstart", "touchmove", "mousedown", "keydown", "wheel"];

export const App = () => {
	const { client, isConnected, setIsConnected, isSubscriptionBroken, setIsSubscriptionBroken } =
		useFridgeApolloClient();
	const dispatch = useAppDispatch();

	// A zombie (stream dead, network up) must show the connection modal like an outage, but WITHOUT
	// re-breaking dismiss-on-DNS-recovery for real outages. Distinguish by episode history.
	const droppedThisEpisodeRef = useRef(false);
	droppedThisEpisodeRef.current = nextDroppedThisEpisode(
		droppedThisEpisodeRef.current,
		isConnected,
		isSubscriptionBroken,
	);
	const isZombie = computeIsZombie(isConnected, isSubscriptionBroken, droppedThisEpisodeRef.current);
	const showConnectionError = !isConnected || isZombie;

	// App must NOT subscribe to appState: that subscription re-rendered App on every idle/active edge
	// and (pre-memoization) rebuilt the Apollo client, restarting INITIAL_STATE_QUERY (the
	// handleSetInitialData refire bug). The wake callback reads the store at invocation time instead.
	const wakeOnAction = useIdleWakeAction();

	const locale = getLocale();

	useEffect(() => {
		setLocale(locale);
		changeLanguage(locale);
	}, [locale]);

	// Stable so react-idle-timer doesn't re-register its handler if App ever re-renders.
	const onPresenceChange = useCallback(
		(presence: PresenceType) => {
			dispatch(setAppState(presence.type as AppState));
		},
		[dispatch],
	);

	return (
		<ApolloProvider client={client}>
			<IdleTimerProvider
				timeout={Config.activityMonitor.inactiveTimeout}
				events={IDLE_ACTIVITY_EVENTS}
				onAction={wakeOnAction}
				onPresenceChange={onPresenceChange}
			>
				<DeviceContextProvider
					client={client}
					appConnected={isConnected}
					setIsConnected={setIsConnected}
					isSubscriptionBroken={isSubscriptionBroken}
					setIsSubscriptionBroken={setIsSubscriptionBroken}
				>
					{/* Mounted once for the app's lifetime so the open-door alarm MP3s are fetched a single
					    time per app session and reused across every order (the kiosk has the HTTP cache
					    disabled — see AlarmAudioProvider). */}
					<AlarmAudioProvider>
						<FlowHost />
						{shouldTraceNetworkDebug() && <NetworkDebugOverlay />}
						{showConnectionError ? (
							<ConnectionErrorModal
								appConnected={isConnected}
								isSubscriptionBroken={isSubscriptionBroken}
								isZombie={isZombie}
							/>
						) : null}
					</AlarmAudioProvider>
				</DeviceContextProvider>
			</IdleTimerProvider>
		</ApolloProvider>
	);
};
